import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Cascader } from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../../../theme/utils';
import { DriveItemId } from '../../../../models/Types';
import {
  getCurrentFolderItem,
  getPartialCurrentFolderState,
  RootReducerState,
} from '../../../../apps/main/rootReducer';
import { useSelector } from 'react-redux';
import { DownOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { isDriveItemFolder } from '../../util';
import { FolderMap } from '../../reducers/currentFolder';
import { CascaderOption } from '@prio365/prio365-react-library/lib/Cascader/components/Cascader';

const useStyles = makePrioStyles((theme) => ({
  root: {
    cursor: 'pointer',
  },
  cascaderDropdownIcon: {
    fontSize: 10,
    lineHeight: 1,
    marginLeft: theme.old.spacing.unit(0.5),
    '& > svg': {
      marginBottom: 1,
    },
  },
  breadcrumbSeparator: {
    margin: '0px 4px',
    color: 'rgba(0,0,0,0.45)',
  },
  popup: {
    position: 'fixed',
    '& .prio-cascader-menus': {
      marginTop: '8px',
    },
    '& ul': {
      marginBottom: '0px',
    },
    '&.prio-cascader-direction-left .prio-cascader-menus': {
      transform: 'translateX(calc(-100% + 32px))',
    },
  },
}));

interface Option {
  value: string;
  label: React.ReactNode;
  customLabel?: React.ReactNode;
  children?: Option[];
}

interface BreadcrumbItem {
  index: number;
  label: string;
  id?: string;
  isHidden?: boolean;
}

const renderLabel = (
  driveItemId: DriveItemId,
  label: string,
  pathPrefix?: string,
  disableLinkNavigation?: boolean,
  onClick?: (driveItemId: DriveItemId) => void,
  className?: string
) => {
  const handleOnClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClick) {
      onClick(driveItemId);
    }
  };

  return (
    <span
      className={className}
      title={label === '...' ? undefined : label}
      onClick={handleOnClick}
    >
      {!disableLinkNavigation && driveItemId ? (
        <Link to={`${pathPrefix ?? ''}folder/${driveItemId}`}>{label}</Link>
      ) : (
        label
      )}
    </span>
  );
};

const useCascaderOptions: (
  breadcrumbItems: BreadcrumbItem[],
  pathPrefix?: string,
  disableLinkNavigation?: boolean,
  onOptionClick?: (driveItemId: DriveItemId) => void
) => Option[] = (
  breadcrumbItems,
  pathPrefix,
  disableLinkNavigation,
  onOptionClick
) => {
  const driveItemIds = useMemo(() => {
    return breadcrumbItems.map(({ id }) => id);
  }, [breadcrumbItems]);

  const partialCurrentFolderState = useSelector<RootReducerState, FolderMap>(
    (state) => getPartialCurrentFolderState(state, driveItemIds)
  );

  return useMemo(
    () =>
      [
        breadcrumbItems
          .filter(({ isHidden }) => isHidden)
          .map(({ id, label }) => ({
            value: id,
            label: renderLabel(
              id,
              label,
              pathPrefix,
              disableLinkNavigation,
              onOptionClick,
              'prio-documents-active-breadcrumb-item'
            ),
            children: (
              partialCurrentFolderState[id]?.value?.filter((item) =>
                isDriveItemFolder(item)
              ) ?? []
            )
              .sort((a, b) => {
                if (breadcrumbItems.find((i) => i.id === a.id)) {
                  return -1;
                }
                if (breadcrumbItems.find((i) => i.id === b.id)) {
                  return 1;
                }
                return a.name.localeCompare(b.name);
              })
              .map<Option>((item) => ({
                value: item.id,
                label: renderLabel(
                  item.id,
                  item.name,
                  pathPrefix,
                  disableLinkNavigation,
                  onOptionClick,
                  breadcrumbItems.find((i) => i.id === item.id)
                    ? 'prio-documents-active-breadcrumb-item'
                    : null
                ),
              })),
          }))
          .reduceRight<Option>((map, option) => {
            if (map) {
              const _children = [...option.children];
              const _index = _children.findIndex(
                (child) => child.value === map.value
              );
              _children.splice(_index, 1, map);
              if (_index > -1) {
                return {
                  ...option,
                  children: _children,
                };
              }
              return option;
            }
            return option;
          }, null),
      ].filter((option) => option),
    [
      breadcrumbItems,
      partialCurrentFolderState,
      pathPrefix,
      disableLinkNavigation,
      onOptionClick,
    ]
  );
};

interface CascaderBreadcrumbItemProps {
  className?: string;
  pathPrefix?: string;
  breadcrumbItems: BreadcrumbItem[];
  isFullPathAvailable: boolean;
  disableLinkNavigation?: boolean;
  onCascaderContentClick?: (driveItemId: DriveItemId) => void;
  onCascaderOptionClick?: (driveItemId: DriveItemId) => void;
  directionLeft?: boolean;
}

export const CascaderBreadcrumbItem: React.FC<CascaderBreadcrumbItemProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const {
    className,
    breadcrumbItems,
    isFullPathAvailable,
    pathPrefix,
    directionLeft = false,
    disableLinkNavigation,
    onCascaderContentClick,
    onCascaderOptionClick,
  } = props;
  const classes = useStyles();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [openFolderDropdown, setOpenFolderDropdown] = useState<boolean>(false);

  const options: Option[] = useCascaderOptions(
    breadcrumbItems,
    pathPrefix ?? '',
    disableLinkNavigation,
    (driveItemId) => {
      setOpenFolderDropdown(false);
      if (onCascaderOptionClick) {
        onCascaderOptionClick(driveItemId);
      }
    }
  );

  const firstItemParentId = useSelector<RootReducerState, DriveItemId>(
    (state) =>
      breadcrumbItems.filter(({ isHidden }) => !isHidden).length > 0
        ? getCurrentFolderItem(
            state,
            breadcrumbItems.filter(({ isHidden }) => !isHidden)[0]?.id
          )?.parentReference?.id
        : null
  );
  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  if (!isFullPathAvailable || options.length > 0) {
    return (
      <span className={classNames(classes.root, className)}>
        {options.length > 0 ? (
          <Cascader
            dropdownClassName={classes.popup}
            options={options as CascaderOption[]}
            fieldNames={{
              label: 'label',
              value: 'value',
              children: 'children',
            }}
            onChange={(value: string[]) => {
              setOpenFolderDropdown(false);
              onCascaderOptionClick?.(value[value.length - 1]);
            }}
            directionLeft={directionLeft}
            open={openFolderDropdown}
            displayRender={(labels, selectedOptions: any[]) =>
              selectedOptions?.[selectedOptions.length - 1]?.customLabel ??
              labels.join(' / ')
            }
            onOpenChange={setOpenFolderDropdown}
          >
            <span>
              {renderLabel(
                firstItemParentId,
                '...',
                pathPrefix,
                disableLinkNavigation,
                (driveItemId) => {
                  onCascaderContentClick(driveItemId);
                }
              )}
              <DownOutlined className={classes.cascaderDropdownIcon} />
            </span>
          </Cascader>
        ) : (
          <span>
            {renderLabel(
              firstItemParentId,
              '...',
              pathPrefix,
              disableLinkNavigation,
              onCascaderContentClick
            )}
          </span>
        )}
        <span className={classes.breadcrumbSeparator}>/</span>
      </span>
    );
  }

  return <span />;
};

export default CascaderBreadcrumbItem;
